import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import EditProviderUser from "./pages/auth/EditProviderUser";
import ForgotPassword from "./pages/auth/ForgotPassword";
import { ProtectedRoute } from "./ProtectedRoute";
import HomePage from "./pages/home";
import ContactUs from "./pages/contact-us";
import Info from "./pages/info";
import Patients from "./pages/patients";
import RequestOptionPage from "./pages/request_option/page.jsx";
import PatientSupport from "./pages/patient_support/index.jsx";
import ProviderSupport from "./pages/provider_support/index.jsx";
import MedicalPortal from "./pages/medical_portal/index.jsx";
import AssistanceRequests from "./pages/assistance_requests";
import AssistanceRequestsNew from "./pages/assistance_requests/new.jsx";
// import AssistanceRequestsByUpload from "./pages/assistance_requests/by-upload.jsx";
import AssistanceRequestsEdit from "./pages/assistance_requests/edit.jsx";
import AssistanceRequestsShow from "./pages/assistance_requests/show";
import AssistanceRequestsRefill from "./pages/assistance_requests/refill.jsx";
import PatientHome from "./pages/patient_portal/home/index.jsx";
import axios from "axios";
import { getThemeColor, getProgramLogo, getManufacturerLogo } from "./libs/global.js";
import useGlobalConfig from "./store/global";
import { LoaderCircle } from "lucide-react";
import PatientSignIn from "./pages/patient_portal/auth/SignIn";
import PatientEnrollmentForm from "./pages/patient_portal/auth/SignUp";
import EditPatientUser from "./pages/patient_portal/auth/EditPatientUser";
import PatientForgotPassword from "./pages/patient_portal/auth/ForgotPassword";
import ClaimStatus from "./pages/patient_portal/claim_status/index.jsx";
import PatientContact from "./pages/patient_portal/contact/index.jsx";
import PatientAssistanceRequests from "./pages/patient_portal/assistance_requests/index.jsx";
import CompassionateRequests from "./pages/requests/compassionate/index.jsx";
import FreetrialRequest from "./pages/requests/freetrail/index.jsx";
import FreetrialRequestFormShow from "./pages/requests/freetrail/detail.jsx";
import CompassionateRequestFormShow from "./pages/requests/compassionate/detail.jsx";
import RequestConsent from "./pages/assistance_requests/consent/page.jsx";
import NewBiRequest from "./pages/requests/bi-request/new.jsx";
import BiRequestList from "./pages/requests/bi-request/index.jsx";
import BIRequestDetails from "./pages/requests/bi-request/details.jsx";
import Verify from "./pages/auth/Verify";
import { ProtectedPatientRoute } from "./ProjectPatientRoute.jsx";
import NotFound from "./pages/not-found";
import Payments from "./pages/payments/index.jsx";
import InitiateForm from "./pages/requests/freetrail/initiate.jsx";
import ReactGA from "react-ga4";

const isProd = import.meta.env.VITE_ENV === "production";

const App = () => {
  const [pageLoading, setPageLoading] = useState(true);
  const renderRef = useRef(false);

  const {
    program,
    updateProgram,
    updateTheme,
    updateProgramLogo,
    updateManufacturerLogo
  } = useGlobalConfig();

  const loadData = async () => {
    if (renderRef.current) {
      renderRef.current = false;
      return;
    }

    try {
      let { data: program } = await axios.get("programs/current");
      const name = program.name;
      updateProgram(program);
      updateTheme(getThemeColor[name]);
      updateProgramLogo(getProgramLogo[name]);
      updateManufacturerLogo(getManufacturerLogo[name]);

      // TODO: use zustand to instead
      localStorage.setItem("Program", JSON.stringify(program));
      localStorage.setItem("themeColor", getThemeColor[name]);
      localStorage.setItem("programLogo", getProgramLogo[name]);
      localStorage.setItem("manufacturerLogo", getManufacturerLogo[name]);
    } catch (e) {
      console.error(e);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const isPatient = window.location.hostname.indexOf("my") >= 0;

  const allowFreeTrialAccess = ["wilate", "nuwiq"].includes(program?.name?.toLowerCase());

  useEffect(() => {
    if (isProd) {
      ReactGA.initialize([
        {
          trackingId: "G-45YB0R4H6F",
        }
      ]);
    }
  }, []);

  if (pageLoading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <LoaderCircle className="animate-spin w-10 h-10" />
      </div>
    );
  }

  if (isPatient) {
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              localStorage.getItem("Authorization") ? (
                <Navigate to="/claim_status" />
              ) : (
                <PatientHome />
              )
            }
          />
          <Route path="/patient/signin" element={
            localStorage.getItem("Authorization") ? (
              <Navigate to="/claim_status" replace />
            ) : (
              <PatientSignIn />
            )
          } />
          <Route path="/patient/signup" element={<PatientEnrollmentForm />} />
          <Route path="/patient/contact" element={<PatientContact />} />
          <Route path="/patient/forgot-password" element={<PatientForgotPassword />} />
          <Route path="/" element={<ProtectedPatientRoute />}>
            <Route path="/claim_status" element={<ClaimStatus />} />
            <Route path="/patient_users/edit" element={<EditPatientUser />} />
            <Route path="/patient/assistance_request/index" element={<PatientAssistanceRequests />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            localStorage.getItem("Authorization") ? (
              <Navigate to={"/patients"} />
            ) : (
              <HomePage />
            )
          }
        />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/verify" element={<Verify />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/info" element={<Info />} />
        <Route path="/patient_support" element={<PatientSupport />} />
        <Route path="/provider_support" element={<ProviderSupport />} />
        <Route path="/medical_portal" element={<MedicalPortal />} />
        <Route path="/" element={<ProtectedRoute />}>
          <Route path="/patients" element={<Patients />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/requests/special-requests" element={<BiRequestList />} />
          <Route path="/requests/bi-request/:biRequestId" element={<BIRequestDetails />} />
          <Route path="/requests/special-requests/new" element={<NewBiRequest />} />
          <Route path="/requests/compassionate_access_requests" element={<CompassionateRequests />} />
          <Route
            path="/requests/compassionate_access_requests/:compassionateRequestId"
            element={<CompassionateRequestFormShow />}
          />
          <Route 
            path="/requests/free_trial_requests" 
            element={allowFreeTrialAccess ? <FreetrialRequest /> : <NotFound />} 
          />
          <Route 
            path="/requests/free_trial_requests/new" 
            element={allowFreeTrialAccess ? <InitiateForm /> : <NotFound />} 
          />
          <Route 
            path="/requests/free_trial_requests/:freetrialRequestId" 
            element={allowFreeTrialAccess ? <FreetrialRequestFormShow /> : <NotFound />} 
          />
          <Route path="/provider_users/edit" element={<EditProviderUser />} />
          <Route
            path="/patients/:patientId/assistance_requests"
            element={<AssistanceRequests />}
          />
          <Route path="/assistance_requests/option" element={<RequestOptionPage />} />
          <Route
            path="/assistance_requests/new"
            element={<AssistanceRequestsNew />}
          />
          <Route path="/assistance_requests/consent" element={<RequestConsent />} />
          {/* <Route
            path="/assistance_requests/by-upload"
            element={<AssistanceRequestsByUpload />}
          /> */}
          <Route
            path="/assistance_requests/:assistanceRequestId/edit"
            element={<AssistanceRequestsEdit />}
          />
          <Route
            path="/assistance_requests/:assistanceRequestId/show"
            element={<AssistanceRequestsShow />}
          />
          <Route
            path="/assistance_requests/:assistanceRequestId/refill"
            element={<AssistanceRequestsRefill />}
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
